export interface PreregistrationLabel {
  es: string
  en: string
}
export interface PreregistrationOption {
  key: string
  label: PreregistrationLabel
  referenceId?: string | null
  photo?: {
    assetId: string
    width: number | null
    height: number | null
  } | null
}
export interface PreregistrationQuestion {
  key: string
  kind?: PreregistrationQuestionKind
  required?: boolean
  label: PreregistrationLabel
  options: PreregistrationOption[]
  maxSelections: number
}
export type PreregistrationQuestionKind =
  | 'artists'
  | 'genres'
  | 'event_types'
  | 'budget'
  | 'group_size'
  | 'motivation'
  | 'barriers'
  | 'readiness'
  | 'custom'
export interface PreregistrationOptionDraft {
  key?: string
  label: PreregistrationLabel
  referenceId?: string | null
  editorId: string
}
export interface PreregistrationQuestionDraft {
  key?: string
  kind: PreregistrationQuestionKind
  required?: boolean
  label: PreregistrationLabel
  options: PreregistrationOptionDraft[]
  maxSelections: number
  editorId: string
}
export interface PreregistrationReference {
  id: string
  label: PreregistrationLabel
}
export interface PreregistrationQuestionType {
  value: PreregistrationQuestionKind
  translationKey: string
  label: string
  prompt: PreregistrationLabel
  catalog: boolean
  defaultMaxSelections: number
  choices: PreregistrationReference[]
}
export interface PreregistrationFormOptions {
  eventStatus?: string
  eventVersion?: number
  questionTypes: PreregistrationQuestionType[]
}
export interface PreregistrationCatalogPage {
  data: PreregistrationReference[]
  meta: {
    currentPage: number
    itemsPerPage: number
    totalItems: number
    totalPages: number
  }
  links: { current: string; next?: string }
}
export interface PreregistrationCampaign {
  eventId: string
  version: number
  enabled: boolean
  opensAt: string
  closesAt: string
  closedAt?: string | null
  priorityMinutes: number
  priorityStartsAt: string | null
  questionnaireVersion: number
  questions: PreregistrationQuestion[]
  processingNotice: PreregistrationLabel
  noticeVersion: string
}
export interface PreregistrationCity {
  id: string
  label: string
}
export interface PreregistrationAccess {
  state: 'public' | 'before_sale' | 'priority' | 'unconfigured'
  canPurchase: boolean
  saleStartsAt: string | null
  publicStartsAt: string | null
  enrolled: boolean
}
export interface PublicPreregistration {
  event: {
    id: string
    name: string
    slug: string
    status: 'preregistration' | 'published'
    verticalBannerAssetId?: string | null
  }
  campaign: PreregistrationCampaign | null
  promotionalAudioAssetId: string | null
  access: PreregistrationAccess
  customer: {
    displayName: string
    email: string
    residence: PreregistrationCity | null
  } | null
  enrollment: { id: string; submittedAt: string } | null
}
export interface PreregistrationGuest {
  dataProcessingPolicyAccepted: true
  dataProcessingPolicyVersion: string
  loginIdentifier: string
  emailConfirmation: string
  thirdPartyDocumentTypeId: string
  documentNumber: string
  firstName: string
  secondName: string | null
  firstLastName: string
  secondLastName: string | null
  phone: string | null
}
export interface PreregistrationRegistrationOptions {
  documentTypes: Array<{ id: string; code: string; name: string }>
  dataProcessingPolicyVersion: string
}
export interface PreregistrationSubmission {
  submissionKey: string
  questionnaireVersion: number
  residenceCityId: string
  attendanceCityIds: string[]
  answers: Array<{ questionKey: string; optionKeys: string[] }>
  dataProcessingPolicyVersion?: string
  processingAccepted: boolean
  noticeVersion: string
  marketingAccepted: boolean
  guest?: PreregistrationGuest
}
export interface PreregistrationReceipt {
  id: string
  submittedAt: string
  activationRequired: boolean
}
export interface PreregistrationAnalytics {
  total: number
  marketingOptIns: number
  answered: number
  choices: Array<{
    questionnaireVersion: number
    questionKey: string
    optionKey: string
    questionLabel: PreregistrationLabel
    optionLabel: PreregistrationLabel
    questionKind?: PreregistrationQuestionKind
    referenceId?: string | null
    count: number
  }>
  regions?: Array<{ regionId: string; label: string | null; count: number }>
  accounts?: { newThirdParties: number; activated: number; pending: number }
  attendance: Array<{ cityId: string; label: string; count: number }>
}
export function toggleSurveyChoice(
  selected: string[],
  option: string,
  maxSelections: number
): string[] {
  if (selected.includes(option))
    return selected.filter((value) => value !== option)
  if (maxSelections === 1) return [option]
  return selected.length < maxSelections ? [...selected, option] : selected
}
export function surveyQuestionIsComplete(
  question: PreregistrationQuestion,
  selected: string[]
): boolean {
  return (
    (!question.required || selected.length > 0) &&
    selected.length <= question.maxSelections &&
    new Set(selected).size === selected.length &&
    selected.every((key) =>
      question.options.some((option) => option.key === key)
    )
  )
}
export function registrationIsOpen(
  value: PublicPreregistration,
  now: number
): boolean {
  const campaign = value.campaign
  return Boolean(
    campaign?.enabled &&
    !campaign.closedAt &&
    value.event.status === 'preregistration' &&
    now >= Date.parse(campaign.opensAt) &&
    now < Date.parse(campaign.closesAt) &&
    (!value.access.saleStartsAt || now < Date.parse(value.access.saleStartsAt))
  )
}

export interface PreregistrationParticipant {
  id: string
  name: string
  documentNumber: string
  email: string | null
  phone: string | null
  submittedAt: string
  activatedAt: string | null
  residence: string | null
  newThirdParty: boolean
  activated: boolean
}
export interface PreregistrationParticipantQuery {
  page: number
  limit: number
  search: string
  cohort: 'all' | 'new' | 'activated' | 'pending'
}
export interface PreregistrationParticipantPage {
  data: PreregistrationParticipant[]
  meta: { currentPage: number; itemsPerPage: number; totalItems: number; totalPages: number }
  links: { current: string; next?: string }
}
